import { useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "../lib/router";
import { shortKey } from "../lib/scopes";

export const inputCls =
  "w-full rounded-xl border-0 bg-surface px-3 py-2 text-sm shadow-sm ring-1 ring-line outline-none transition placeholder:text-faint focus:ring-2 focus:ring-haze-deep dark:focus:ring-haze/60";

type ButtonVariant = "default" | "primary" | "danger" | "ghost";

const buttonCls = (variant: ButtonVariant, size: "md" | "sm") =>
  `inline-flex items-center justify-center rounded-full font-medium transition-all duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40 ${
    { md: "px-3.5 py-1.5 text-sm", sm: "px-2.5 py-1 text-xs" }[size]
  } ${
    {
      default: "bg-surface shadow-sm ring-1 ring-line hover:bg-fill-subtle",
      // Brand primary: ink with an Arctic Haze glow on hover.
      primary:
        "bg-ink text-paper shadow-sm hover:shadow-[0_0_16px_rgba(193,232,251,0.8)] active:bg-slate dark:bg-[#f9fdff] dark:text-ink dark:hover:shadow-[0_0_24px_rgba(193,232,251,0.9)]",
      // Quiet red: red text, a light red ground on hover.
      danger: "text-danger hover:bg-danger/10",
      ghost: "text-muted hover:bg-fill",
    }[variant]
  }`;

/**
 * Every button. `primary` is the one main action of a view, `default` a normal one, `ghost` a quiet one, `danger` deletes.
 * `size="sm"` is the small one, for rows, toolbars and popups; never a hand-set padding or text size.
 */
export function Button({
  variant = "default",
  size = "md",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "md" | "sm" }) {
  return <button {...rest} className={`${buttonCls(variant, size)} ${className}`} />;
}

/** A link that looks like a Button: `to` a page of the app, `href` a file or another site (which opens in a new tab). */
export function ButtonLink({
  to,
  href,
  variant = "default",
  size = "md",
  title,
  className = "",
  children,
}: {
  to?: string;
  href?: string;
  variant?: ButtonVariant;
  size?: "md" | "sm";
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  const cls = `${buttonCls(variant, size)} ${className}`;
  if (to) return <Link to={to} title={title} className={cls}>{children}</Link>;
  const external = !!href && /^https?:\/\//.test(href);
  return (
    <a href={href} title={title} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className={cls}>
      {children}
    </a>
  );
}

const STATUS_DOT: Record<string, { dot: string; text: string }> = {
  queued: { dot: "bg-faint", text: "text-muted" },
  running: { dot: "bg-info", text: "text-info" },
  succeeded: { dot: "bg-success", text: "text-muted" },
  failed: { dot: "bg-danger", text: "text-danger" },
  cancelled: { dot: "bg-warning", text: "text-warning" },
};

/** Quiet by default: a coloured dot and a label; only failures and running work draw attention. */
export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_DOT[status] ?? STATUS_DOT.queued!;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.text}`}>
      <span className="relative flex size-2">
        {status === "running" && <span className={`absolute inline-flex size-full animate-ping rounded-full opacity-60 ${s.dot}`} />}
        <span className={`relative inline-flex size-2 rounded-full ${s.dot}`} />
      </span>
      {status}
    </span>
  );
}

export function Badge({ children, tone = "zinc" }: { children: ReactNode; tone?: "zinc" | "amber" | "red" | "blue" | "emerald" | "violet" | "teal" }) {
  const t = {
    zinc: "bg-fill text-muted",
    amber: "bg-warning/10 text-warning",
    red: "bg-danger/10 text-danger",
    blue: "bg-haze-soft text-slate dark:bg-haze/15 dark:text-haze",
    emerald: "bg-success/10 text-success",
    violet: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
    teal: "bg-teal-500/10 text-teal-700 dark:text-teal-300",
  }[tone];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tiny font-medium ${t}`}>{children}</span>;
}

/** What a schedule is about: its run works under that scope. Shown by type and name; a scope with no name by its short key. */
export function SubjectBadge({ subjectKey, subjectName }: { subjectKey: string | null; subjectName?: string | null }) {
  if (!subjectKey) return null;
  const type = subjectKey.slice(0, subjectKey.indexOf(":"));
  const label = subjectName ? `${type.charAt(0).toUpperCase()}${type.slice(1)} · ${subjectName}` : shortKey(subjectKey);
  return (
    <span className="shrink-0" title={`About scope ${subjectKey}: its run works under it`}>
      <Badge>{label}</Badge>
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-4 pt-6 pb-4 sm:flex-row sm:items-end sm:justify-between md:px-6 md:pt-8">
      <div className="min-w-0">
        <h1 className="text-title font-semibold tracking-tight">{title}</h1>
        {subtitle && <div className="mt-1 max-w-3xl text-sm text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`card ${className}`}>{children}</div>;
}

/** What a list or a table is: its title, a line saying what it is for, and its actions. Sits above the box, never around it. */
export function SectionHead({ title, hint, action }: { title?: ReactNode; hint?: ReactNode; action?: ReactNode }) {
  if (!title && !hint && !action) return null;
  return (
    <div className="mb-3">
      {(title || action) && (
        <div className="flex min-h-8 items-center justify-between gap-3">
          {title ? <h2 className="min-w-0 text-heading font-semibold tracking-tight">{title}</h2> : <span />}
          {action && <div className="flex shrink-0 flex-wrap gap-2">{action}</div>}
        </div>
      )}
      {hint && <p className="mt-0.5 text-sm text-muted">{hint}</p>}
    </div>
  );
}

/** One part of a page that is not a list: its title, hint and actions above a white card. */
export function Panel({ title, hint, action, children, className = "" }: { title: ReactNode; hint?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`min-w-0 ${className}`}>
      <SectionHead title={title} hint={hint} action={action} />
      <div className="card p-5">{children}</div>
    </section>
  );
}

/** Tiny trend line; values oldest → newest. */
export function Sparkline({ values, className = "" }: { values: number[]; className?: string }) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const points = values.map((v, i) => [(i / (values.length - 1)) * 100, 26 - (v / max) * 22] as const);
  const line = points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const id = `spark-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className={`h-8 w-full overflow-visible ${className}`} aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L100,28 L0,28 Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

/** A Field around several controls (radios, a dropdown of choices), which a label must not wrap. */
export function FieldGroup({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div role="group" aria-label={label}>
      <div className="mb-1.5 text-xs font-medium text-muted">{label}</div>
      {children}
      {hint && <div className="mt-1.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

/** One option of a row of rounded tabs: the Skills page's levels, the Requests views, a skill's "Applies to". */
export const pillCls = (active: boolean) =>
  `rounded-full px-3 py-1 text-sm transition ${active ? "bg-raised font-medium shadow-sm" : "text-muted hover:text-fg"}`;

/**
 * A form's choice of one option among a few, drawn as the pill bar the tabs use: a skill's "Applies to", an agent's
 * owners and who can use it. `label` names the choice for screen readers; a label may carry an icon. `compact` is the bar
 * for a row, its options icons alone, each named by its `title`, which is its tooltip and what a screen reader says.
 */
export function Choice<T extends string>({ label, value, options, onChange, disabled = false, compact = false }: {
  label: string; value: T; options: { value: T; label: ReactNode; title?: string }[]; onChange: (value: T) => void; disabled?: boolean; compact?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={`flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-full bg-fill ${compact ? "p-0.5" : "p-1"}`}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} aria-label={o.title} title={o.title} disabled={disabled}
          className={`${compact ? `rounded-full p-1.5 transition ${value === o.value ? "bg-raised text-fg shadow-sm ring-1 ring-line" : "text-muted hover:text-fg"}` : pillCls(value === o.value)} inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60`}
          onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Json({ value, className = "" }: { value: unknown; className?: string }) {
  return (
    <pre className={`scroll-thin overflow-auto rounded-xl bg-fill p-3 font-mono text-xs ${className}`}>
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

/** A fold. `action` sits at the end of its header, beside the part that opens it, for what acts on everything inside. */
export function Collapsible({ summary, children, defaultOpen = false, action }: { summary: ReactNode; children: ReactNode; defaultOpen?: boolean; action?: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          <span className={`inline-block text-tiny text-faint transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
          {summary}
        </button>
        {action}
      </div>
      {open && <div className="mt-1.5 pl-4">{children}</div>}
    </div>
  );
}

export function ErrorText({ error }: { error: string | null }) {
  return error ? <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm break-words text-danger">{error}</p> : null;
}

/** An agent chooses a skill, or how to use a tool, by one line: shorter than this, it says too little (AIT-27). */
export const AGENT_LINE_MIN = 30;

/**
 * A warning, never a refusal, under a skill's description or a connector's usage guide whose first line is missing or
 * too short for an agent to tell when to use it: that one line is all a run is shown before it chooses.
 */
export function AgentLineWarning({ text, what }: { text: string; what: "skill" | "tool" }) {
  const line = text.split("\n").find((l) => l.trim())?.trim() ?? "";
  if (line.length >= AGENT_LINE_MIN) return null;
  return (
    <span className="mt-1.5 block text-xs text-warning">
      {line ? "This first line is short" : "There is no first line"}: agents can't tell when to use this. They see only {what === "skill" ? "the description" : "the guide's first line"} before they choose, so say in one line what it is for and when to use it.
    </span>
  );
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function duration(start: string | null, end: string | null): string {
  if (!start) return "—";
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export const cost = (usd: number | null | undefined) => (usd === null || usd === undefined ? "—" : `$${usd.toFixed(4)}`);

/** "4 Oct", with the year when it is not this one: a date on a line a person scans rather than reads. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  const sameYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString("en-GB", { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}

/** 57866 → "57.9k" */
export const compact = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

/** A picture when there is one, otherwise initials on a soft colour that stays the same for a name. `xs` sits inside a Badge. */
export function Avatar({ name, src, size = "md" }: { name: string; src?: string | null; size?: "xs" | "sm" | "md" | "lg" }) {
  const [failed, setFailed] = useState(false);
  const palette = [
    "bg-haze text-slate", "bg-emerald-100 text-success", "bg-violet-100 text-violet-800", "bg-amber-100 text-warning",
    "bg-rose-100 text-rose-800", "bg-sky-100 text-sky-800", "bg-lime-100 text-lime-800", "bg-fuchsia-100 text-fuchsia-800",
  ];
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
  const dims = size === "xs" ? "size-4 text-[8px]" : size === "sm" ? "size-6 text-[10px]" : size === "lg" ? "size-16 text-xl" : "size-8 text-xs";
  // A picture that will not load (an outside logo that has moved, a face only its own site will serve) falls back
  // to the initials rather than leaving a hole.
  if (src && !failed) {
    return <img src={src} alt="" onError={() => setFailed(true)} className={`shrink-0 rounded-full bg-raised object-cover ring-1 ring-line ${dims}`} />;
  }
  return <span className={`inline-grid shrink-0 place-items-center rounded-full font-semibold ${dims} ${palette[hash % palette.length]}`}>{initials}</span>;
}
